import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Send, Trash2, Users, Mail, Phone, CheckCircle2, Clock, Search, UserPlus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface Friend {
  id: number;
  friendEmail?: string;
  friendPhone?: string;
  friendName?: string;
  friendUsername?: string;
  addedAt: string;
  isRegistered?: boolean;
  registeredUser?: {
    id: number;
    username: string;
    email: string;
    createdAt: string;
  } | null;
}

interface Competition {
  id: number;
  title: string;
}

interface Challenge {
  id: number;
  name: string;
}

interface SearchUser {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export default function Friends() {
  const { toast } = useToast();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<Friend | null>(null);
  const [inviteType, setInviteType] = useState<'competition' | 'challenge'>('challenge');
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch friends list
  const { data: friendsData, isLoading: friendsLoading } = useQuery({
    queryKey: ['/api/friends'],
  });

  const friends = (friendsData as { friends: Friend[] })?.friends || [];

  // Fetch competitions list
  const { data: competitionsData } = useQuery({
    queryKey: ['/api/competitions/featured'],
  });

  const competitions = (competitionsData as Competition[]) || [];

  // Fetch challenges list
  const { data: challengesData } = useQuery({
    queryKey: ['/api/v1/challenges/active'],
  });

  const challenges = (challengesData as { challenges: Challenge[] })?.challenges || [];

  // Search users query
  const { data: searchData, isLoading: isSearching, refetch: searchUsers } = useQuery({
    queryKey: [`/api/friends/search-users?query=${searchQuery}`],
    enabled: false, // Manual trigger only
  });

  const searchResults = (searchData as { users: SearchUser[] })?.users || [];

  // Add existing user mutation
  const addExistingUserMutation = useMutation({
    mutationFn: async (registeredUserId: number) => {
      return await apiRequest('POST', '/api/friends/add-existing', { 
        body: { registeredUserId } 
      });
    },
    onSuccess: () => {
      toast({
        title: "Friend Added!",
        description: "User has been added to your friend list.",
      });
      setShowSearchDialog(false);
      setSearchQuery('');
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add friend",
        variant: "destructive",
      });
    },
  });

  // Send invite mutation
  const sendInviteMutation = useMutation({
    mutationFn: async (inviteData: {
      friendId: number;
      competitionId?: number;
      challengeId?: number;
    }) => {
      return await apiRequest('POST', '/api/friends/invite', { body: inviteData });
    },
    onSuccess: () => {
      toast({
        title: "Invitation Sent!",
        description: "Your friend will receive an SMS invitation shortly.",
      });
      setShowInviteDialog(false);
      setSelectedFriend(null);
      setSelectedItemId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
    },
  });

  // Remove friend mutation
  const removeFriendMutation = useMutation({
    mutationFn: async (friendId: number) => {
      return await apiRequest('DELETE', `/api/friends/${friendId}`);
    },
    onSuccess: () => {
      toast({
        title: "Friend Removed",
        description: "Friend has been removed from your list.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove friend",
        variant: "destructive",
      });
    },
  });

  const handleSendInvite = () => {
    if (!selectedFriend || !selectedItemId) {
      toast({
        title: "Error",
        description: "Please select a friend and a competition/challenge",
        variant: "destructive",
      });
      return;
    }

    sendInviteMutation.mutate({
      friendId: selectedFriend.id,
      competitionId: inviteType === 'competition' ? selectedItemId : undefined,
      challengeId: inviteType === 'challenge' ? selectedItemId : undefined,
    });
  };

  const openInviteDialog = (friend: Friend) => {
    if (!friend.friendPhone) {
      toast({
        title: "Cannot Invite",
        description: "This friend doesn't have a phone number",
        variant: "destructive",
      });
      return;
    }
    setSelectedFriend(friend);
    setShowInviteDialog(true);
  };

  const handleSearch = () => {
    if (searchQuery.trim().length < 3) {
      toast({
        title: "Search Query Too Short",
        description: "Please enter at least 3 characters to search",
        variant: "destructive",
      });
      return;
    }
    searchUsers();
  };

  if (friendsLoading) {
    return <div>Loading friends...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            My Friends
          </h2>
          <p className="text-muted-foreground mt-1">
            Invite your friends to competitions and challenges
          </p>
        </div>
        <Button 
          onClick={() => setShowSearchDialog(true)}
          data-testid="button-add-to-friend-list"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add to Friend List
        </Button>
      </div>

      {/* Friends List */}
      {friends.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Friends Yet</h3>
            <p className="text-muted-foreground">
              Visit a challenge or competition page and click "Invite a Friend to PrizeDrop" to add friends
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {friends.map((friend) => (
            <Card key={friend.id} data-testid={`card-friend-${friend.id}`}>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">
                          {friend.friendName || friend.friendUsername || "Friend"}
                        </h3>
                        {friend.isRegistered ? (
                          <Badge variant="default" className="bg-green-500 hover:bg-green-600" data-testid={`badge-status-registered-${friend.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Registered
                          </Badge>
                        ) : (
                          <Badge variant="secondary" data-testid={`badge-status-pending-${friend.id}`}>
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>
                      {friend.friendUsername && (
                        <p className="text-sm text-muted-foreground">
                          @{friend.friendUsername}
                        </p>
                      )}
                      {friend.isRegistered && friend.registeredUser && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Joined as @{friend.registeredUser.username}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFriendMutation.mutate(friend.id)}
                      data-testid={`button-remove-friend-${friend.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {friend.friendEmail && (
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Mail className="h-3 w-3 mr-2" />
                        {friend.friendEmail}
                      </div>
                    )}
                    {friend.friendPhone && (
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Phone className="h-3 w-3 mr-2" />
                        {friend.friendPhone}
                      </div>
                    )}
                  </div>
                  {!friend.isRegistered && (
                    <Button
                      className="w-full"
                      onClick={() => openInviteDialog(friend)}
                      disabled={!friend.friendPhone}
                      data-testid={`button-invite-friend-${friend.id}`}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Invite to Competition
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Friend</DialogTitle>
            <DialogDescription>
              Select a competition or challenge to invite {selectedFriend?.friendName || "your friend"} to
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Invite Type</Label>
              <Select
                value={inviteType}
                onValueChange={(value: 'competition' | 'challenge') => {
                  setInviteType(value);
                  setSelectedItemId(null);
                }}
              >
                <SelectTrigger data-testid="select-invite-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="challenge">Challenge</SelectItem>
                  <SelectItem value="competition">Competition</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                {inviteType === 'competition' ? 'Select Competition' : 'Select Challenge'}
              </Label>
              <Select
                value={selectedItemId?.toString() || ""}
                onValueChange={(value) => setSelectedItemId(parseInt(value))}
              >
                <SelectTrigger data-testid="select-item">
                  <SelectValue placeholder={`Choose a ${inviteType}`} />
                </SelectTrigger>
                <SelectContent>
                  {inviteType === 'competition'
                    ? competitions.map((comp) => (
                        <SelectItem key={comp.id} value={comp.id.toString()}>
                          {comp.title}
                        </SelectItem>
                      ))
                    : challenges.map((challenge) => (
                        <SelectItem key={challenge.id} value={challenge.id.toString()}>
                          {challenge.name}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteDialog(false)}
              data-testid="button-cancel-invite"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendInvite}
              disabled={!selectedItemId || sendInviteMutation.isPending}
              data-testid="button-send-invite"
            >
              {sendInviteMutation.isPending ? "Sending..." : "Send SMS Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Friend List Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add to Friend List</DialogTitle>
            <DialogDescription>
              Search for existing users by phone number or email address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input
                placeholder="27821234567 or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="input-search-users"
              />
              <Button 
                onClick={handleSearch} 
                disabled={isSearching}
                data-testid="button-execute-search"
              >
                {isSearching ? "Searching..." : "Search"}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold">Search Results</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {searchResults.map((user) => (
                    <Card key={user.id} data-testid={`card-search-result-${user.id}`}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">
                              {user.firstName || user.lastName 
                                ? `${user.firstName || ''} ${user.lastName || ''}`.trim() 
                                : user.username}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              @{user.username}
                            </p>
                            <div className="flex gap-3 mt-1">
                              {user.email && (
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {user.email}
                                </div>
                              )}
                              {user.phone && (
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <Phone className="h-3 w-3 mr-1" />
                                  {user.phone}
                                </div>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => addExistingUserMutation.mutate(user.id)}
                            disabled={addExistingUserMutation.isPending}
                            data-testid={`button-add-user-${user.id}`}
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Add Friend
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {searchData && searchResults.length === 0 && !isSearching && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No users found matching your search</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
